<template>
  <div class="detail-modal-content">
    <div id="Loader" v-if="loading">
      <div class="spinner-bar">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
    <b-row class="chart-row">
      <b-col class="col-6 left-charts">
        <b-card class="left-chart-card default-border1" body-class="p-0" header-tag="header">
          <h6 slot="header">{{ chartTitles.groundAccel }}</h6>
          <div id="frfDetailChart1" class="graph"></div>
        </b-card>
        <b-card class="left-chart-card default-border1 mt10" body-class="p-0" header-tag="header">
          <h6 slot="header">{{ chartTitles.structureAccel }}</h6>
          <div id="frfDetailChart2" class="graph"></div>
        </b-card>
      </b-col>
      <b-col class="col-6 right-charts">
        <b-card class="right-chart-card default-border1" body-class="p-0" header-tag="header">
          <h6 slot="header">{{ chartTitles.frfMagnitude }}</h6>
          <div id="frfDetailChart3" class="graph"></div>
        </b-card>
        <b-card class="right-chart-card default-border1 mt10" body-class="p-0" header-tag="header">
          <h6 slot="header">{{ chartTitles.frfPhase }}</h6>
          <div id="frfDetailChart4" class="graph"></div>
        </b-card>
        <b-card class="right-chart-card default-border1 mt10" body-class="p-0" header-tag="header">
          <h6 slot="header">{{ chartTitles.coherence }}</h6>
          <div id="frfDetailChart5" class="graph"></div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
var Plotly = require("plotly.js/dist/plotly.min.js");
import chartUtil from "../common/chartUtil";
import utils from "../common/utils";
import moment from "moment";

export default {
  props: {
    rowData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    chartTitles() {
      const groundDeviceName = (this.rowData && this.rowData.ground_device_name) ? this.rowData.ground_device_name : "";
      const structureDeviceName = (this.rowData && this.rowData.structure_device_name) ? this.rowData.structure_device_name : "";
      const axisLabel = (this.rowData && this.rowData.axis_label) ? this.rowData.axis_label : "";
      
      return {
        groundAccel: `${groundDeviceName}${axisLabel ? " " + axisLabel : ""} 지반 가속도 Raw Data`,
        structureAccel: `${structureDeviceName}${axisLabel ? " " + axisLabel : ""} 구조물 가속도 Raw Data`,
        frfMagnitude: "FRF Magnitude (로그 스케일)",
        frfPhase: "FRF Phase",
        coherence: "Coherence",
      };
    },
    hasChartData() {
      return (
        (this.chartData.ground && this.chartData.ground.time && this.chartData.ground.value && this.chartData.ground.value.length > 0) ||
        (this.chartData.structure && this.chartData.structure.time && this.chartData.structure.value && this.chartData.structure.value.length > 0) ||
        (this.chartData.output && this.chartData.output.freq_hz && this.chartData.output.freq_hz.length > 0)
      );
    },
  },
  data() {
    return {
      loading: false,
      chartData: {
        ground: null,
        structure: null,
        output: null,
      },
    };
  },
  mounted() {
    if (this.rowData && this.rowData.idx) {
      this.fetchChartData();
    } else {
      this.initCharts();
    }
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  watch: {
    rowData: {
      handler(newVal) {
        if (newVal && newVal.idx) {
          this.fetchChartData();
        } else {
          this.$nextTick(() => {
            this.initCharts();
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    fetchChartData() {
      if (!this.rowData || !this.rowData.idx) {
        this.initCharts();
        return;
      }

      this.loading = true;
      const reqBody = {
        idx: this.rowData.idx,
      };

      this.$http
        .post("/frequencyResponseFunction/getChartData", reqBody, {
          timeout: 60000,
        })
        .then((res) => {
          this.loading = false;
          if (res.data.resultyn === "Y" && res.data.data) {
            const data = res.data.data;
            this.chartData = {
              ground: data.ground || null,
              structure: data.structure || null,
              output: data.output || null,
            };
            this.$emit('chart-data-updated', this.hasChartData);
            this.drawChartsWithData(data);
          } else {
            this.chartData = {
              ground: null,
              structure: null,
              output: null,
            };
            this.$emit('chart-data-updated', false);
            this.initCharts();
            if (res.data.errormsg) {
              console.error("차트 데이터 조회 실패:", res.data.errormsg);
            }
          }
        })
        .catch((err) => {
          this.loading = false;
          this.chartData = {
            ground: null,
            structure: null,
            output: null,
          };
          this.$emit('chart-data-updated', false);
          console.error("차트 데이터 조회 오류:", err);
          this.initCharts();
        });
    },
    drawChartsWithData(data) {
      this.$nextTick(() => {
        if (data.ground && data.ground.time && data.ground.value) {
          this.drawTimeLineChart("frfDetailChart1", {
            x: data.ground.time,
            y: data.ground.value,
            name: "ground",
          });
        } else {
          this.drawTimeLineChart("frfDetailChart1", {
            x: [],
            y: [],
            name: "ground",
          });
        }

        if (data.structure && data.structure.time && data.structure.value) {
          this.drawTimeLineChart("frfDetailChart2", {
            x: data.structure.time,
            y: data.structure.value,
            name: "structure",
          });
        } else {
          this.drawTimeLineChart("frfDetailChart2", {
            x: [],
            y: [],
            name: "structure",
          });
        }

        if (data.output && data.output.freq_hz && data.output.frf_mag) {
          this.drawFrequencyChart("frfDetailChart3", {
            x: data.output.freq_hz,
            y: data.output.frf_mag,
            name: "frf_mag",
          });
        } else {
          this.drawFrequencyChart("frfDetailChart3", {
            x: [],
            y: [],
            name: "frf_mag",
          });
        }

        if (data.output && data.output.freq_hz && data.output.frf_phase) {
          this.drawFrequencyChart("frfDetailChart4", {
            x: data.output.freq_hz,
            y: data.output.frf_phase,
            name: "frf_phase",
          });
        } else {
          this.drawFrequencyChart("frfDetailChart4", {
            x: [],
            y: [],
            name: "frf_phase",
          });
        }

        if (data.output && data.output.freq_hz && data.output.coherence) {
          this.drawFrequencyChart("frfDetailChart5", {
            x: data.output.freq_hz,
            y: data.output.coherence,
            name: "coherence",
          });
        } else {
          this.drawFrequencyChart("frfDetailChart5", {
            x: [],
            y: [],
            name: "coherence",
          });
        }
      });
    },
    initCharts() {
      this.chartData = {
        ground: null,
        structure: null,
        output: null,
      };
      this.$emit('chart-data-updated', false);
      this.$nextTick(() => {
        this.drawTimeLineChart("frfDetailChart1", {
          x: [],
          y: [],
          name: "rawfile",
        });
        this.drawTimeLineChart("frfDetailChart2", {
          x: [],
          y: [],
          name: "rawfile",
        });
        this.drawFrequencyChart("frfDetailChart3", {
          x: [],
          y: [],
          name: "rawfile",
        });
        this.drawFrequencyChart("frfDetailChart4", {
          x: [],
          y: [],
          name: "rawfile",
        });
        this.drawFrequencyChart("frfDetailChart5", {
          x: [],
          y: [],
          name: "rawfile",
        });
      });
    },
    drawTimeLineChart(id, options, range) {
      let data = [
        {
          x: options.x,
          y: options.y,
          type: "scatter",
          mode: "lines",
          line: { width: 1 },
        },
      ];

      let layout = chartUtil.setTimeLayout(id, {
        margin: {
          l: 90,
          r: 30,
          t: 30,
          b: 60,
          pad: 10,
        },
        annotation: options.annotation,
        showlegend: false,
      });
      layout.xaxis.nticks = 6;
      layout.xaxis.title = "시간";
      layout.yaxis.title = {
        text: "가속도(g)",
        standoff: 30,
      };
      layout.autosize = true;
      delete layout.width;
      delete layout.height;

      let config = {
        responsive: true,
        displayModeBar: false,
      };

      Plotly.newPlot(id, data, layout, config);
      
      this.$nextTick(() => {
        Plotly.Plots.resize(id);
      });
    },
    drawFrequencyChart(id, options, range) {
      let data = [
        {
          x: options.x,
          y: options.y,
          type: "scatter",
          mode: "lines",
          line: { width: 1 },
        },
      ];
      let layout = chartUtil.setLayout(id, {
        margin: {
          l: 50,
          r: 30,
          t: 30,
          b: 60,
          pad: 10,
        },
        annotation: options.annotation,
        showlegend: false,
      });
      layout.xaxis.nticks = 6;
      layout.xaxis.title = "주파수(Hz)";
      
      if (id === "frfDetailChart3") {
        layout.yaxis.type = "log";
        layout.yaxis.dtick = 1;
        layout.yaxis.tickformat = "~g";
      }
      
      if (id === "frfDetailChart4" || id === "frfDetailChart5") {
        if (id === "frfDetailChart4") {
          layout.yaxis.title = "Radian";
        }
        layout.margin.l = 70;
      }
      
      layout.autosize = true;
      delete layout.width;
      delete layout.height;

      let config = {
        responsive: true,
        displayModeBar: false,
      };

      Plotly.newPlot(id, data, layout, config);
      
      this.$nextTick(() => {
        Plotly.Plots.resize(id);
        
        if (id === "frfDetailChart5" && options.y && options.y.length > 0) {
          const isStringType = typeof options.y[0] === 'string';
          if (isStringType) {
            const chartElement = document.getElementById(id);
            if (chartElement) {
              const yAxisTicks = chartElement.querySelectorAll('.ytick text');
              const maxLength = 6;
              
              yAxisTicks.forEach(tick => {
                const originalText = tick.textContent;
                if (originalText && originalText.length > maxLength) {
                  tick.textContent = originalText.substring(0, maxLength) + '...';
                }
              });
            }
          }
        }
      });
    },
    destroyCharts() {
      const chartIds = [
        "frfDetailChart1",
        "frfDetailChart2",
        "frfDetailChart3",
        "frfDetailChart4",
        "frfDetailChart5",
      ];
      
      chartIds.forEach((id) => {
        try {
          Plotly.purge(id);
        } catch (err) {
          console.error(`차트 삭제 오류 (${id}):`, err);
        }
      });
    },
    downloadExcel() {
      if (!this.hasChartData) {
        return;
      }

      this.loading = true;
      let contents = [];

      if (this.chartData.ground && this.chartData.ground.time && this.chartData.ground.value) {
        const groundContent = {
          time: this.chartData.ground.time,
          value: this.chartData.ground.value,
        };
        const groundSheetName = this.chartTitles.groundAccel.split("가속도 Raw")[0].trim();
        contents.push({
          sheetName: groundSheetName,
          header: ["time", "value"],
          content: groundContent,
        });
      }

      if (this.chartData.structure && this.chartData.structure.time && this.chartData.structure.value) {
        const structureContent = {
          time: this.chartData.structure.time,
          value: this.chartData.structure.value,
        };
        const structureSheetName = this.chartTitles.structureAccel.split("가속도 Raw")[0].trim();
        contents.push({
          sheetName: structureSheetName,
          header: ["time", "value"],
          content: structureContent,
        });
      }

      if (this.chartData.output && this.chartData.output.freq_hz) {
        const outputContent = {
          freq_hz: this.chartData.output.freq_hz,
        };
        const outputHeader = ["freq_hz"];

        if (this.chartData.output.frf_mag) {
          outputHeader.push("frf_mag");
          outputContent.frf_mag = this.chartData.output.frf_mag;
        }
        if (this.chartData.output.frf_phase) {
          outputHeader.push("frf_phase");
          outputContent.frf_phase = this.chartData.output.frf_phase;
        }
        if (this.chartData.output.coherence) {
          outputHeader.push("coherence");
          if (this.chartData.output.coherence.length > 0 && typeof this.chartData.output.coherence[0] === 'string') {
            outputContent.coherence = this.chartData.output.coherence;
          } else {
            outputContent.coherence = this.chartData.output.coherence;
          }
        }

        contents.push({
          sheetName: "FRF 분석 결과",
          header: outputHeader,
          content: outputContent,
        });
      }

      if (contents.length === 0) {
        this.loading = false;
        return;
      }

      const analtime = (this.rowData && this.rowData.analtime) ? this.rowData.analtime : "";
      const axis = (this.rowData && this.rowData.axis_label) ? this.rowData.axis_label : "";
      const fileName = `FRF_상세분석_${analtime ? moment(analtime).format("YYYYMMDD_HHmmss") : moment().format("YYYYMMDD_HHmmss")}_${axis || "전체"}`;

      const reqBody = {
        fileName: fileName,
        data: contents,
      };

      this.$http
        .post("/frequencyResponseFunction/excelDownload", reqBody, {
          responseType: "arraybuffer",
          timeout: 180000,
        })
        .then((res) => {
          this.loading = false;
          utils.getExcelFile(res, null, fileName);
        })
        .catch((err) => {
          this.loading = false;
          console.error("엑셀 다운로드 오류:", err);
        });
    },
  },
};
</script>

<style lang="scss">
.detail-modal-content {
  padding: 20px;
  width: 100%;
  height: 100%;
  position: relative;
  
  & #Loader {
    z-index: 10;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  
  & .chart-row {
    height: 100%;
    margin: 0;
    
    & .col {
      height: 100%;
      padding: 0 10px;
      display: flex;
      flex-direction: column;
      
      &:first-child {
        padding-left: 0;
      }
      
      &:last-child {
        padding-right: 0;
      }
      
      &.left-charts {
        & .left-chart-card {
          flex: 1;
          height: calc(50% - 5px);
          display: flex;
          flex-direction: column;
          padding: 5px !important;
          
          &.mt10 {
            margin-top: 10px;
          }
        }
      }
      
      &.right-charts {
        & .right-chart-card {
          flex: 1;
          height: calc(33.333% - 7px);
          display: flex;
          flex-direction: column;
          padding: 5px !important;
          
          &.mt10 {
            margin-top: 10px;
          }
        }
      }
      
      & .card {
        & .card-header {
          padding: 6px 15px 8px;
          border-bottom: 1px solid #404656;
          flex-shrink: 0;
          
          & h6 {
            font-size: 15px;
            line-height: unset;
            justify-content: center;
            margin: 0;
          }
        }
        
        & .card-body {
          flex: 1;
          overflow: hidden;
          
          & .graph {
            height: 100%;
            width: 100%;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/analysis/FrequencyResponseFunctionDetailModal.vue